<template>
  <div class="gift-recipient-page">
    <el-card shadow="never">
      <template slot="header">
        <div class="card-head">
          <div>
            <span class="title"><i class="el-icon-present" style="color: #409eff; margin-right: 6px;"></i>礼品名单管理</span>
            <span class="muted" style="margin-left: 12px; font-size: 13px;">维护可领取礼品/产品的人员名单（支持客户姓名模糊检索、批量导入与导出）</span>
          </div>
          <div class="head-tools">
            <el-button v-if="$hasPerm('func:gift-recipient:import')" type="warning" size="small" icon="el-icon-upload2" @click="openImportDialog">批量导入</el-button>
            <el-button type="success" size="small" icon="el-icon-download" :loading="exporting" @click="handleExport">导出 Excel</el-button>
            <el-button
              v-if="$hasPerm('func:gift-recipient:delete')"
              type="danger"
              size="small"
              icon="el-icon-delete"
              :disabled="!selectedRows.length"
              @click="handleBatchDelete"
            >
              批量删除{{ selectedRows.length ? ' (' + selectedRows.length + ')' : '' }}
            </el-button>
          </div>
        </div>
      </template>

      <!-- 搜索筛选栏与右侧新建 -->
      <div class="search-row-wrap">
        <el-form :inline="true" size="small" class="search-bar" @submit.native.prevent="handleSearch">
          <el-form-item label="客户姓名">
            <el-input
              v-model="searchName"
              placeholder="客户、配偶姓名"
              clearable
              prefix-icon="el-icon-search"
              style="width: 240px"
              @clear="handleSearch"
              @keyup.enter.native="handleSearch"
            >
            </el-input>
          </el-form-item>

          <el-form-item label="开发人">
            <el-input
              v-model="searchDeveloper"
              placeholder="开发人"
              clearable
              style="width: 180px"
              @clear="handleSearch"
              @keyup.enter.native="handleSearch"
            >
            </el-input>
          </el-form-item>

          <el-form-item label="客户负责人">
            <el-input
              v-model="searchManager"
              placeholder="客户负责人"
              clearable
              style="width: 180px"
              @clear="handleSearch"
              @keyup.enter.native="handleSearch"
            >
            </el-input>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>

        <div class="search-row-right">
          <el-button v-if="$hasPerm('func:gift-recipient:create')" type="primary" size="small" icon="el-icon-plus" @click="openDialog()">新建名单</el-button>
        </div>
      </div>

      <!-- 名单数据表格 -->
      <el-table
        :data="list"
        border
        stripe
        size="small"
        v-loading="loading"
        @selection-change="handleSelectionChange"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column type="selection" width="45" align="center"></el-table-column>
        <el-table-column type="index" label="序号" width="65" align="center" :index="calcIndex"></el-table-column>
        <el-table-column prop="customerName" label="客户姓名" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <el-link type="primary" :underline="false" style="font-weight: 600; font-size: 13px;" @click="jumpToReport(row.customerName, row)">
              {{ row.customerName }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column prop="developer" label="开发人" min-width="120" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <el-link v-if="row.developer" type="primary" :underline="false" @click="jumpToReport(null, { developer: row.developer })">
              {{ row.developer }}
            </el-link>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="manager" label="客户负责人" min-width="120" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <el-link v-if="row.manager" type="primary" :underline="false" @click="jumpToReport(null, { manager: row.manager })">
              {{ row.manager }}
            </el-link>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="130" align="center">
          <template slot-scope="{ row }">
            <span>{{ formatTime(row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="$hasPerm('func:gift-recipient:edit') || $hasPerm('func:gift-recipient:delete')" label="操作" width="200" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button v-if="$hasPerm('func:gift-recipient:edit')" type="text" size="small" icon="el-icon-edit" @click="openDialog(row)">编辑</el-button>
            <el-button v-if="$hasPerm('func:gift-recipient:delete')" type="text" size="small" icon="el-icon-delete" style="color: #F56C6C;" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-wrap">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        >
        </el-pagination>
      </div>
    </el-card>

    <!-- 新建 / 编辑弹窗 -->
    <el-dialog
      :title="dialogForm.id ? '编辑礼品名单' : '新建礼品名单'"
      :visible.sync="dialogVisible"
      width="480px"
      :close-on-click-modal="false"
      @closed="resetDialogForm"
    >
      <el-form ref="dialogFormRef" :model="dialogForm" :rules="rules" label-width="95px" size="small">
        <el-form-item label="客户姓名" prop="customerName">
          <el-input v-model="dialogForm.customerName" placeholder="请输入客户姓名（如：张三 或 张三、李四）" clearable></el-input>
        </el-form-item>
        <el-form-item label="开发人" prop="developer">
          <el-input v-model="dialogForm.developer" placeholder="请输入开发人姓名" clearable></el-input>
        </el-form-item>
        <el-form-item label="客户负责人" prop="manager">
          <el-input v-model="dialogForm.manager" placeholder="请输入客户负责人姓名" clearable></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>

    <!-- 批量导入弹窗 -->
    <el-dialog
      title="批量导入礼品名单"
      :visible.sync="importDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      @closed="resetImport"
    >
      <div style="margin-bottom: 16px;">
        <p style="font-size: 13px; color: #606266; margin-bottom: 10px; line-height: 1.6;">
          1. 请先下载标准 Excel 导入模板，按列填入<strong>客户姓名、开发人、客户负责人</strong>。<br/>
          2. 客户姓名支持输入单人或合称（如：<code>张三</code> 或 <code>张三、李四</code>）。<br/>
          3. 上传文件支持 <code>.xlsx</code> / <code>.xls</code> 格式。
        </p>
        <el-button type="text" icon="el-icon-download" @click="handleDownloadTemplate">点击下载《礼品名单导入模板.xlsx》</el-button>
      </div>

      <el-upload
        ref="uploadRef"
        drag
        action=""
        :auto-upload="false"
        :limit="1"
        accept=".xlsx, .xls"
        :on-change="handleFileChange"
        :on-remove="handleFileRemove"
        style="text-align: center;"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将 Excel 文件拖到此处，或<em>点击上传</em></div>
        <div class="el-upload__tip" slot="tip">仅支持单次上传 1 个 Excel 文件</div>
      </el-upload>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="importDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="importing" :disabled="!importFile" @click="handleDoImport">开始导入</el-button>
      </div>
    </el-dialog>

    <!-- 批量导入冲突确认弹窗 -->
    <el-dialog
      title="发现相似客户 - 请确认处理方式"
      :visible.sync="importConflictVisible"
      width="780px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        style="margin-bottom: 14px;"
      >
        <template slot="title">
          <span style="font-size: 13px; font-weight: bold;">
            系统检测到以下 {{ importConflicts.length }} 条导入数据与现有客户高度重合（姓名包含且开发人、业务员均一致）
          </span>
        </template>
        <div style="font-size: 12px; margin-top: 4px; color: #606266;">
          您可以逐条选择是「合并为一条」（更新已有记录为完整合称）还是「独立保存」（创建新档案）。<br/>
          注：名字虽有包含关系但业务员不同的记录已自动作为独立记录导入，无需在此确认。
        </div>
      </el-alert>

      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <span style="font-size: 13px; color: #606266; font-weight: 600;">待确认名单列表：</span>
        <div>
          <el-button size="mini" type="primary" plain @click="setAllConflictsAction('merge')">全部设为【合并】</el-button>
          <el-button size="mini" type="info" plain @click="setAllConflictsAction('create')">全部设为【独立保存】</el-button>
        </div>
      </div>

      <el-table :data="importConflicts" border stripe size="mini" max-height="360" :header-cell-style="{ background: '#f5f7fa', color: '#606266' }">
        <el-table-column type="index" label="序号" width="55" align="center"></el-table-column>
        <el-table-column prop="customerName" label="本次导入姓名" min-width="140">
          <template slot-scope="{ row }">
            <span style="font-weight: 600; color: #303133;">{{ row.customerName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="matchedName" label="系统已有姓名" min-width="130">
          <template slot-scope="{ row }">
            <span style="color: #e6a23c; font-weight: 600;">{{ row.matchedName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="developer" label="开发人" width="100" align="center"></el-table-column>
        <el-table-column prop="manager" label="业务员(负责人)" width="120" align="center"></el-table-column>
        <el-table-column label="处理选择" width="180" align="center">
          <template slot-scope="{ row }">
            <el-radio-group v-model="row.action" size="mini">
              <el-radio-button label="merge">合并</el-radio-button>
              <el-radio-button label="create">独立保存</el-radio-button>
            </el-radio-group>
          </template>
        </el-table-column>
      </el-table>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="importConflictVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="importConfirming" @click="handleConfirmImport">确认并执行导入</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { giftRecipientApi } from '../api/api'

export default {
  name: 'GiftRecipient',
  data () {
    return {
      loading: false,
      exporting: false,
      importing: false,
      submitting: false,
      list: [],
      total: 0,
      page: 1,
      size: 20,

      // 搜索项
      searchName: '',
      searchDeveloper: '',
      searchManager: '',

      selectedRows: [],

      // 新建/编辑弹窗
      dialogVisible: false,
      dialogForm: {
        id: null,
        customerName: '',
        developer: '',
        manager: ''
      },
      rules: {
        customerName: [{ required: true, message: '请输入客户姓名', trigger: 'blur' }],
        developer: [{ required: true, message: '请输入开发人姓名', trigger: 'blur' }],
        manager: [{ required: true, message: '请输入客户负责人姓名', trigger: 'blur' }]
      },

      // 导入弹窗
      importDialogVisible: false,
      importFile: null,

      // 导入冲突处理
      importConflictVisible: false,
      importDirectList: [],
      importConflicts: [],
      importConfirming: false
    }
  },
  created () {
    this.fetchData()
  },
  methods: {
    calcIndex (idx) {
      return (this.page - 1) * this.size + idx + 1
    },
    formatTime (t) {
      if (!t) return '-'
      return t.substring(0, 10)
    },
    jumpToReport (name, row) {
      const query = {
        type: '礼品',
        dateType: 'day'
      }
      if (name) {
        query.customer = name
      }
      if (row) {
        if (row.developer && row.developer.trim()) {
          query.developer = row.developer.trim()
        }
        if (row.manager && row.manager.trim()) {
          query.manager = row.manager.trim()
        }
      }
      this.$router.push({
        path: '/report',
        query
      })
    },
    scopeParams () {
      const me = (function () { try { return JSON.parse(sessionStorage.getItem('user') || '{}') } catch (e) { return {} } })()
      const isSuper = me && (me.role === 'super_admin' || me.role === '超级管理员' || Number(me.roleId) === 1)
      const isAdmin = me && !isSuper && (me.role === 'admin' || Number(me.roleId) === 2)
      const isSales = me && !isSuper && !isAdmin && Number(me.roleId) === 3
      if (isAdmin && me.id) return { mineForUserId: me.id }
      if (isSales && me.id) return { salesmanUserId: me.id }
      return {}
    },
    fetchData () {
      this.loading = true
      const params = Object.assign({
        page: this.page,
        size: this.size,
        name: this.searchName.trim() || undefined,
        developer: this.searchDeveloper.trim() || undefined,
        manager: this.searchManager.trim() || undefined
      }, this.scopeParams())
      giftRecipientApi.getPage(params).then(res => {
        const d = res.data || {}
        this.list = d.records || []
        this.total = d.total || 0
      }).catch(err => {
        console.error(err)
        this.$message.error('获取名单失败')
      }).finally(() => {
        this.loading = false
      })
    },
    handleSearch () {
      this.page = 1
      this.fetchData()
    },
    handleReset () {
      this.searchName = ''
      this.searchDeveloper = ''
      this.searchManager = ''
      this.page = 1
      this.fetchData()
    },
    handleSelectionChange (val) {
      this.selectedRows = val
    },
    handleSizeChange (v) {
      this.size = v
      this.page = 1
      this.fetchData()
    },
    handleCurrentChange (v) {
      this.page = v
      this.fetchData()
    },

    // 新增 / 编辑
    openDialog (row) {
      if (row) {
        this.dialogForm = {
          id: row.id,
          customerName: row.customerName || '',
          developer: row.developer || '',
          manager: row.manager || ''
        }
      } else {
        this.dialogForm = {
          id: null,
          customerName: '',
          developer: '',
          manager: ''
        }
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.dialogFormRef) this.$refs.dialogFormRef.clearValidate()
      })
    },
    resetDialogForm () {
      this.dialogForm = { id: null, customerName: '', developer: '', manager: '' }
    },
    handleSubmit () {
      this.$refs.dialogFormRef.validate(valid => {
        if (!valid) return
        this.submitting = true
        const isEdit = !!this.dialogForm.id

        if (isEdit) {
          giftRecipientApi.update(this.dialogForm).then(() => {
            this.$message.success('编辑成功')
            this.dialogVisible = false
            this.fetchData()
          }).catch(err => {
            console.error(err)
            this.$message.error('保存失败，请重试')
          }).finally(() => {
            this.submitting = false
          })
          return
        }

        // 新建时：先进行重合客户检测（规则：姓名有包含关系 + 开发人相同 + 业务员相同）
        giftRecipientApi.checkDuplicate(this.dialogForm).then(res => {
          const d = res.data || {}
          if (d.hasDuplicate) {
            const matchedName = d.matchedName
            const matchedDev = d.developer || '无'
            const matchedMgr = d.manager || '无'
            const matchedId = d.matchedId
            const curName = this.dialogForm.customerName

            this.$confirm(
              `<div style="line-height: 1.6;">
                检测到系统已有客户 <strong style="color:#e6a23c;">【${matchedName}】</strong>，且开发人（<strong>${matchedDev}</strong>）与业务员（<strong>${matchedMgr}</strong>）均完全一致。<br/><br/>
                请问是否需要与已有客户<strong>合并为同一条记录</strong>？<br/>
                <span style="font-size:12px; color:#909399;">
                  ・ 点击【合并为一条】：将更新已有档案名称为【${curName}】<br/>
                  ・ 点击【作为独立记录】：将新建一条独立的客户档案
                </span>
              </div>`,
              '疑似重复客户确认',
              {
                dangerouslyUseHTMLString: true,
                distinguishCancelAndClose: true,
                confirmButtonText: '合并为一条',
                cancelButtonText: '作为独立记录',
                type: 'warning'
              }
            ).then(() => {
              // 用户选择合并
              this.doSaveItem({ ...this.dialogForm, mergeTargetId: matchedId, matchedName: matchedName })
            }).catch(action => {
              if (action === 'cancel') {
                // 用户选择作为独立记录保存
                this.doSaveItem(this.dialogForm)
              } else {
                // 取消/关闭弹窗
                this.submitting = false
              }
            })
          } else {
            // 无重合，正常新建
            this.doSaveItem(this.dialogForm)
          }
        }).catch(err => {
          console.error(err)
          this.doSaveItem(this.dialogForm)
        })
      })
    },
    doSaveItem (payload) {
      giftRecipientApi.add(payload).then(res => {
        const d = res.data || {}
        this.$message.success(d.message || '新建成功')
        this.dialogVisible = false
        this.fetchData()
      }).catch(err => {
        console.error(err)
        this.$message.error('保存失败，请重试')
      }).finally(() => {
        this.submitting = false
      })
    },

    // 删除单条
    handleDelete (row) {
      this.$confirm(`确定要删除客户【${row.customerName}】的礼品名单记录吗？`, '删除确认', {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消'
      }).then(() => {
        giftRecipientApi.delete(row.id).then(() => {
          this.$message.success('删除成功')
          if (this.list.length === 1 && this.page > 1) this.page--
          this.fetchData()
        }).catch(() => {
          this.$message.error('删除失败')
        })
      }).catch(() => {})
    },

    // 批量删除
    handleBatchDelete () {
      const ids = this.selectedRows.map(r => r.id)
      if (!ids.length) return
      this.$confirm(`确定要批量删除已选中的 ${ids.length} 条名单记录吗？`, '批量删除确认', {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消'
      }).then(() => {
        giftRecipientApi.batchDelete(ids).then(() => {
          this.$message.success('批量删除成功')
          this.fetchData()
        }).catch(() => {
          this.$message.error('批量删除失败')
        })
      }).catch(() => {})
    },

    // 导出 Excel
    handleExport () {
      this.exporting = true
      const params = Object.assign({
        name: this.searchName.trim() || undefined,
        developer: this.searchDeveloper.trim() || undefined,
        manager: this.searchManager.trim() || undefined
      }, this.scopeParams())
      if (this.selectedRows.length) {
        params.ids = this.selectedRows.map(r => r.id).join(',')
      }
      giftRecipientApi.export(params).then(res => {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = '礼品名单.xlsx'
        link.click()
        URL.revokeObjectURL(link.href)
        this.$message.success('导出成功')
      }).catch(err => {
        console.error(err)
        this.$message.error('导出失败')
      }).finally(() => {
        this.exporting = false
      })
    },

    // 导入相关
    openImportDialog () {
      this.importFile = null
      this.importDialogVisible = true
    },
    resetImport () {
      this.importFile = null
      if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
    },
    handleFileChange (file) {
      this.importFile = file.raw
    },
    handleFileRemove () {
      this.importFile = null
    },
    handleDownloadTemplate () {
      giftRecipientApi.downloadTemplate().then(res => {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = '礼品名单导入模板.xlsx'
        link.click()
        URL.revokeObjectURL(link.href)
      }).catch(() => {
        this.$message.error('模板下载失败')
      })
    },
    handleDoImport () {
      if (!this.importFile) {
        this.$message.warning('请选择需要上传的 Excel 文件')
        return
      }
      this.importing = true
      giftRecipientApi.importPreview(this.importFile).then(res => {
        const d = res.data || {}
        if (!d.success) {
          this.$message.error(d.message || '文件解析失败')
          return
        }
        this.importDirectList = d.directList || []
        this.importConflicts = (d.conflicts || []).map(c => ({
          ...c,
          action: 'merge' // 默认建议合并为一条
        }))

        if (this.importConflicts.length > 0) {
          // 发现重合客户（姓名包含 且 开发人相同 且 业务员相同），弹窗供操作员选择
          this.importConflictVisible = true
        } else {
          // 无任何需要确认的记录（或不同业务员已自动独立入库），直接执行保存
          this.handleConfirmImport()
        }
      }).catch(err => {
        console.error(err)
        this.$message.error('导入预检失败')
      }).finally(() => {
        this.importing = false
      })
    },
    setAllConflictsAction (act) {
      this.importConflicts.forEach(c => {
        c.action = act
      })
    },
    handleConfirmImport () {
      this.importConfirming = true
      giftRecipientApi.importConfirm({
        directList: this.importDirectList,
        conflictResolutions: this.importConflicts
      }).then(res => {
        const d = res.data || {}
        if (d.success) {
          this.$message.success(d.message || '导入成功')
          this.importConflictVisible = false
          this.importDialogVisible = false
          this.importFile = null
          if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
          this.fetchData()
        } else {
          this.$message.error(d.message || '导入失败')
        }
      }).catch(err => {
        console.error(err)
        this.$message.error('导入提交失败')
      }).finally(() => {
        this.importConfirming = false
      })
    }
  }
}
</script>

<style scoped>
.gift-recipient-page {
  padding: 4px;
}
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.card-head .title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.head-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.search-row-wrap {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}
.search-bar {
  margin-bottom: 0;
}
.search-row-right {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.pagination-wrap {
  margin-top: 16px;
  text-align: right;
}
.muted {
  color: #909399;
}
</style>
